import { computed, inject } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { requestMissingTranslations } from 'dashboard/api/messageTranslation';

// One agent-level preference: message bubbles show their completed Chinese
// translation in place of the foreign original. It lives in the agent's UI
// settings, so it survives conversation switches and reloads.
export const CHINESE_DISPLAY_KEY = 'bcw_show_chinese_translation';

export function useChineseDisplay() {
  // Bubbles rendered without the dashboard store (previews, isolated specs) stay in the original.
  if (!inject('store', null)) {
    return { showChinese: computed(() => false), toggleChinese: () => {} };
  }
  const { uiSettings, updateUISettings } = useUISettings();
  const showChinese = computed(
    () => uiSettings.value?.[CHINESE_DISPLAY_KEY] === true
  );
  const toggleChinese = () =>
    updateUISettings({ [CHINESE_DISPLAY_KEY]: !showChinese.value });
  return { showChinese, toggleChinese };
}

// While Chinese display is on, messages never translated are queued for the
// server's background translation: batched per conversation, each message once
// per page session. The server dedupes again and runs no model call inline.
export const MISSING_BATCH = 30;
const FLUSH_DELAY = 300;
const requested = new Set();
const waiting = new Map();
let timer = null;

async function flush() {
  timer = null;
  const batches = [...waiting.entries()];
  waiting.clear();
  await Promise.all(
    batches.map(async ([scope, ids]) => {
      const [accountId, conversationId] = scope.split(':');
      const list = [...ids];
      for (let start = 0; start < list.length; start += MISSING_BATCH) {
        const messageIds = list.slice(start, start + MISSING_BATCH);
        try {
          // eslint-disable-next-line no-await-in-loop
          await requestMissingTranslations({ accountId, conversationId, messageIds });
        } catch {
          // Best effort: the message's own AI button still works; allow a later retry.
          messageIds.forEach(id => requested.delete(`${accountId}:${id}`));
        }
      }
    })
  );
}

export function queueMissingTranslation(accountId, conversationId, messageId) {
  const key = `${accountId}:${messageId}`;
  if (!accountId || !conversationId || !messageId || requested.has(key)) {
    return false;
  }
  requested.add(key);
  const scope = `${accountId}:${conversationId}`;
  if (!waiting.has(scope)) waiting.set(scope, new Set());
  waiting.get(scope).add(messageId);
  if (timer === null) timer = setTimeout(flush, FLUSH_DELAY);
  return true;
}
